import React, { useState, useRef } from 'react';
import { Upload, Video, Check, CheckCircle2, X, AlertCircle, Loader2 } from 'lucide-react';
import apiService from '../../../services/api';
import './AvatarUpload.css';

const AvatarUpload = ({ onSuccess }) => {
  const [formData, setFormData] = useState({ name: '', description: '', video: null });
  const [currentStep, setCurrentStep] = useState(1);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [uploadedVideoUrl, setUploadedVideoUrl] = useState('');
  const [createdAvatar, setCreatedAvatar] = useState(null); // { key, name }
  const fileInputRef = useRef(null);
  const xhrRef = useRef(null);

  const steps = [
    { num: 1, label: '上传视频' },
    { num: 2, label: '创建模型' },
    { num: 3, label: '完成创建' },
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ['video/mp4', 'video/mov', 'video/avi', 'video/quicktime'];
    if (!allowedTypes.includes(file.type) && !file.type.startsWith('video/')) {
      setError('请选择支持的视频格式 (MP4, MOV, AVI)');
      return;
    }
    if (file.size > 500 * 1024 * 1024) {
      setError('视频文件大小不能超过 500MB');
      return;
    }
    if (file.size < 1 * 1024 * 1024) {
      setError('视频文件太小，请选择大于 1MB 的视频文件');
      return;
    }

    setFormData(prev => ({ ...prev, video: file }));
    setError('');
    setUploadProgress(0);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelect({ target: { files: [file] } });
  };

  const handleDragOver = (e) => e.preventDefault();

  // Preview-mode mock: simulate upload progress then succeed
  const mockUpload = () => {
    setUploading(true);
    setError('');
    setUploadProgress(0);
    let pct = 0;
    const timer = setInterval(() => {
      pct += Math.floor(Math.random() * 18) + 8;
      if (pct >= 100) {
        pct = 100;
        clearInterval(timer);
        setUploadProgress(100);
        setUploadedVideoUrl('mock-file-key-preview');
        setTimeout(() => {
          setUploading(false);
          setCurrentStep(2);
        }, 600);
      } else {
        setUploadProgress(pct);
      }
    }, 200);
  };

  const handleVideoUpload = async () => {
    if (!formData.video) {
      setError('请选择视频文件');
      return;
    }

    // Preview mode: skip real XHR
    if (process.env.REACT_APP_UI_PREVIEW === 'true') {
      mockUpload();
      return;
    }

    try {
      setUploading(true);
      setError('');
      setUploadProgress(0);

      const uploadData = new FormData();
      uploadData.append('file', formData.video);

      const xhr = new XMLHttpRequest();
      xhrRef.current = xhr;

      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          setUploadProgress(Math.round((e.loaded / e.total) * 100));
        }
      });

      xhr.addEventListener('load', () => {
        try {
          if (xhr.status === 200) {
            const response = JSON.parse(xhr.responseText);
            if (response.fileKey) {
              setUploadedVideoUrl(response.fileKey);
              setCurrentStep(2);
              setUploadProgress(100);
              setTimeout(() => setUploading(false), 1500);
            } else {
              throw new Error('服务器响应格式错误');
            }
          } else {
            const errorResponse = JSON.parse(xhr.responseText).catch?.() ?? {};
            throw new Error(errorResponse.error || `上传失败 (${xhr.status})`);
          }
        } catch (err) {
          setError('上传失败：' + err.message);
          setUploading(false);
          setUploadProgress(0);
        }
        xhrRef.current = null;
      });

      xhr.addEventListener('error', () => {
        setError('网络错误，上传失败');
        setUploading(false);
        setUploadProgress(0);
        xhrRef.current = null;
      });

      xhr.addEventListener('timeout', () => {
        setError('上传超时，请重试');
        setUploading(false);
        setUploadProgress(0);
        xhrRef.current = null;
      });

      xhr.timeout = 5 * 60 * 1000;
      xhr.open('POST', '/api/upload-video');
      xhr.withCredentials = true;
      xhr.send(uploadData);
    } catch (err) {
      setError('上传失败：' + err.message);
      setUploadProgress(0);
      setUploading(false);
      xhrRef.current = null;
    }
  };

  const handleCancelUpload = () => {
    if (xhrRef.current) {
      xhrRef.current.abort();
      setUploading(false);
      setUploadProgress(0);
      setError('上传已取消');
      xhrRef.current = null;
    }
  };

  const handleCreateModel = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) { setError('请输入数字人名称'); return; }
    if (!uploadedVideoUrl) { setError('请先上传视频'); return; }

    // Preview mode: skip real API call
    if (process.env.REACT_APP_UI_PREVIEW === 'true') {
      setProcessing(true);
      setError('');
      const previewName = formData.name;
      setTimeout(() => {
        setProcessing(false);
        setCreatedAvatar({ key: 'vm-preview-mock-key-001', name: previewName });
        setCurrentStep(3);
      }, 1200);
      return;
    }

    try {
      setProcessing(true);
      setError('');

      const response = await fetch('/api/create-model', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name: formData.name, description: formData.description, fileKey: uploadedVideoUrl }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.error || '创建模型失败');
      }

      const result = await response.json();
      if (result.success === false) throw new Error(result.message || '创建模型失败');

      const avatarKey = result.data?.virtualman_key || result.virtualman_key || '';
      setCreatedAvatar({ key: avatarKey, name: formData.name });
      setCurrentStep(3);
      setFormData({ name: '', description: '', video: null });
      if (fileInputRef.current) fileInputRef.current.value = '';
      setUploadedVideoUrl('');
    } catch (err) {
      setError('创建失败：' + err.message);
    } finally {
      setProcessing(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const resetToStep1 = () => {
    setCurrentStep(1);
    setFormData({ name: '', description: '', video: null });
    setUploadedVideoUrl('');
    setCreatedAvatar(null);
    setError('');
    setUploadProgress(0);
  };

  const handleGoGenerate = () => {
    if (createdAvatar) {
      localStorage.setItem('selectedAvatar', JSON.stringify(createdAvatar));
    }
    window.location.href = '/generate-video';
  };

  return (
    <div className="au-wrap">

      {/* Step indicators */}
      <div className="au-steps">
        {steps.map((step, index) => (
          <React.Fragment key={step.num}>
            <div className="au-step">
              <div className={`au-step-circle ${currentStep > step.num ? 'done' : currentStep === step.num ? 'active' : ''}`}>
                {currentStep > step.num ? <Check size={13} strokeWidth={2.5} /> : step.num}
              </div>
              <span className={`au-step-label ${currentStep >= step.num ? 'active' : ''}`}>{step.label}</span>
            </div>
            {index < steps.length - 1 && (
              <div className={`au-step-line ${currentStep > step.num ? 'done' : ''}`} />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Step 1: Upload video */}
      {currentStep === 1 && (
        <div className="au-card">
          <h2 className="au-card-title">上传训练视频</h2>
          <p className="au-card-desc">上传高质量视频，用于训练您的数字人模型</p>

          <div
            className={`au-dropzone${formData.video ? ' has-file' : ''}`}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onClick={() => !formData.video && fileInputRef.current?.click()}
          >
            <input type="file" ref={fileInputRef} onChange={handleFileSelect} accept="video/*" className="au-file-input" />

            {formData.video ? (
              <div className="au-file-info">
                <div className="au-file-icon"><Video size={20} /></div>
                <div className="au-file-details">
                  <div className="au-file-name">{formData.video.name}</div>
                  <div className="au-file-size">{formatFileSize(formData.video.size)}</div>
                </div>
                <button
                  className="au-file-remove"
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setFormData(p => ({ ...p, video: null })); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                >
                  <X size={14} />
                </button>
              </div>
            ) : (
              <div className="au-dropzone-empty">
                <div className="au-upload-icon"><Upload size={22} /></div>
                <p className="au-upload-title">拖拽视频文件到此处，或点击选择</p>
                <p className="au-upload-hint">支持 MP4、MOV、AVI 格式，文件大小不超过 500MB</p>
              </div>
            )}
          </div>

          {uploading && (
            <div className="au-progress">
              <div className="au-progress-header">
                <Loader2 size={13} className="au-spinner" />
                <span>正在上传视频...</span>
                <span className="au-progress-pct">{Math.round(uploadProgress)}%</span>
              </div>
              <div className="au-progress-track">
                <div className="au-progress-fill" style={{ width: `${uploadProgress}%` }} />
              </div>
              <div className="au-progress-meta">
                <span>{formData.video?.name}</span>
                <span>{uploadProgress < 100 ? '上传中...' : '处理中...'}</span>
              </div>
            </div>
          )}

          {error && (
            <div className="au-error">
              <AlertCircle size={14} />
              {error}
            </div>
          )}

          <div className="au-actions">
            {uploading ? (
              <button type="button" className="au-btn au-btn-ghost" onClick={handleCancelUpload}>
                取消上传
              </button>
            ) : (
              <button type="button" className="au-btn au-btn-primary" onClick={handleVideoUpload} disabled={!formData.video}>
                上传视频
              </button>
            )}
          </div>
        </div>
      )}

      {/* Step 2: Create model */}
      {currentStep === 2 && (
        <form className="au-card" onSubmit={handleCreateModel}>
          <h2 className="au-card-title">设置数字人信息</h2>
          <p className="au-card-desc">填写数字人基本信息，开始创建模型</p>

          <div className="au-notice-success">
            <CheckCircle2 size={16} />
            <div>
              <div className="au-notice-title">视频上传成功</div>
              <div className="au-notice-desc">现在请填写数字人信息来创建模型</div>
            </div>
          </div>

          <div className="au-form-group">
            <label className="au-label" htmlFor="au-name">
              数字人名称 <span className="au-required">*</span>
            </label>
            <input
              className="au-input"
              id="au-name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="请输入数字人名称"
            />
          </div>

          <div className="au-form-group">
            <label className="au-label" htmlFor="au-description">描述</label>
            <textarea
              className="au-textarea"
              id="au-description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="请输入数字人描述（可选）"
              rows={3}
            />
          </div>

          {processing && (
            <div className="au-processing">
              <Loader2 size={15} className="au-spinner" />
              <div>
                <div className="au-processing-title">正在创建数字人模型...</div>
                <div className="au-processing-desc">这可能需要几分钟时间，请耐心等待</div>
              </div>
            </div>
          )}

          {error && (
            <div className="au-error">
              <AlertCircle size={14} />
              {error}
            </div>
          )}

          <div className="au-actions">
            <button type="submit" className="au-btn au-btn-primary" disabled={processing || !formData.name.trim()}>
              {processing ? <><Loader2 size={13} className="au-spinner" /> 创建中...</> : '创建数字人模型'}
            </button>
          </div>
        </form>
      )}

      {/* Step 3: Success */}
      {currentStep === 3 && (
        <div className="au-card au-success-card">
          <div className="au-success-icon"><CheckCircle2 size={28} strokeWidth={1.5} /></div>
          <h2 className="au-success-title">数字人创建成功</h2>
          <p className="au-success-desc">您的数字人已成功创建，现在可以用于视频生成了</p>
          <div className="au-actions">
            <button type="button" className="au-btn au-btn-ghost" onClick={resetToStep1}>
              创建另一个
            </button>
            <button type="button" className="au-btn au-btn-primary" onClick={handleGoGenerate}>
              开始生成视频
            </button>
          </div>
        </div>
      )}

      {/* Upload guidelines */}
      <div className="au-guidelines">
        <h3 className="au-guidelines-title">上传要求</h3>
        <ul className="au-guidelines-list">
          <li>视频时长建议在 30 秒到 5 分钟之间</li>
          <li>视频分辨率建议不低于 720p</li>
          <li>人物应正面朝向镜头，表情自然</li>
          <li>背景简洁，光线充足</li>
          <li>避免快速移动和手势过多</li>
        </ul>
      </div>

    </div>
  );
};

export default AvatarUpload;
